import { useState } from "react";
import { sanitizeResponse } from "../utils/sanitize";
import {
  Container,
  SpaceBetween,
  FormField,
  Textarea,
  Button,
  Alert,
} from "@cloudscape-design/components";
import { bihGenerateClient } from "../utils/bihClient";

// GraphQL query
const callReviseItem = /* GraphQL */ `
  query CallReviseItem(
    $stem: String!
    $options: String!
    $rationales: String!
    $feedback: String!
  ) {
    callReviseItem(
      stem: $stem
      options: $options
      rationales: $rationales
      feedback: $feedback
    ) {
      body
      statusCode
      __typename
    }
  }
`;

export default function ReviseItemInput() {
  const [isLoading, setIsLoading] = useState(false);
  const [stem, setStem] = useState("");
  const [options, setOptions] = useState("");
  const [rationales, setRationales] = useState("");
  const [feedback, setFeedback] = useState("");
  const [outputData, setOutputData] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const client = bihGenerateClient();

  async function handleReviseItem() {
    setIsLoading(true);
    setOutputData("Waiting for response...");
    setShowAlert(false);

    try {
      // Call the GraphQL API which will invoke the Lambda function
      const response = await client.graphql({
        query: callReviseItem,
        variables: {
          stem,
          options,
          rationales,
          feedback,
        },
      });

      // Extract the response from the GraphQL result
      const result = response.data.callReviseItem;

      if (result && result.statusCode === 200 && result.body) {
        try {
          // First parse - the body is a string
          let parsedContent = JSON.parse(result.body);

          // Check if parsedContent has an Item property (new format)
          if (parsedContent && parsedContent.Item) {
            try {
              // Parse the Item content if it's a string
              if (typeof parsedContent.Item === "string") {
                parsedContent.Item = JSON.parse(parsedContent.Item);
              }
              // Display the parsed content
              setOutputData(sanitizeResponse(JSON.stringify(parsedContent.Item, null, 2)));
              setShowAlert(true);
            } catch (itemParseError) {
              console.error("Error parsing Item content:", itemParseError);
              setOutputData(
                `Error parsing Item content: ${itemParseError.message}\nRaw content: ${parsedContent.Item}`
              );
            }
          } else {
            // Handle legacy format or other formats
            // Check if parsedContent is also a string (double-stringified JSON)
            if (typeof parsedContent === "string") {
              try {
                // Second parse if needed
                parsedContent = JSON.parse(parsedContent);
              } catch (innerError) {
                // Inner parse error, use first parse result
              }
            }

            // Display the parsed content
            setOutputData(sanitizeResponse(JSON.stringify(parsedContent, null, 2)));
          }
        } catch (parseError) {
          console.error("Error parsing response body:", parseError);
          setOutputData(
            `Error parsing response: ${parseError.message}\nRaw body: ${result.body}`
          );
        }
      } else if (result) {
        console.error("API returned non-200 status or empty body:", result);
        setOutputData(
          `Error: Status ${result.statusCode || "unknown"}\nBody: ${
            result.body || "empty"
          }`
        );
      } else {
        console.error("Empty result from API");
        setOutputData("Error: Received empty result from API");
      }
    } catch (error) {
      console.error("Error calling GraphQL API:", error);

      // Check if there are GraphQL errors
      if (error.errors && error.errors.length > 0) {
        const errorMessages = error.errors.map((e) => e.message).join("\n");
        setOutputData(`GraphQL Error(s):\n${errorMessages}`);
      } else {
        setOutputData(`Error: ${error.message || "Unknown error"}`);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Alert banner removed as requested */}
        
        <FormField label="Stem" stretch={true}>
          <Textarea
            value={stem}
            onChange={({ detail }) => setStem(detail.value)}
            placeholder="Enter a stem here"
            rows={3}
          />
        </FormField>

        <FormField label="Response Options" stretch={true}>
          <Textarea
            value={options}
            onChange={({ detail }) => setOptions(detail.value)}
            placeholder="A) First option
B) Second option
C) Third option
D) Fourth option"
            rows={4}
          />
        </FormField>

        <FormField label="Response Rationales" stretch={true}>
          <Textarea
            value={rationales}
            onChange={({ detail }) => setRationales(detail.value)}
            placeholder="Rationale A: Explanation for option A
Rationale B: Explanation for option B
Rationale C: Explanation for option C
Rationale D: Explanation for option D"
            rows={4}
          />
        </FormField>

        <FormField label="Feedback Notes" stretch={true}>
          <Textarea
            value={feedback}
            onChange={({ detail }) => setFeedback(detail.value)}
            placeholder="Enter the feedback text here"
            rows={3}
          />
        </FormField>

        <div style={{ textAlign: "center" }}>
          <Button
            variant="primary"
            onClick={handleReviseItem}
            loading={isLoading}
            loadingText="Revising..."
          >
            Revise
          </Button>
        </div>

        <FormField label="Output" stretch={true}>
          <Textarea
            value={outputData}
            onChange={({ detail }) => setOutputData(detail.value)}
            placeholder="Revised item output goes here!!"
            rows={10}
            readOnly={isLoading}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}
